(() => {
  'use strict';
  const element = document.getElementById('pharmacy-cart-config');
  if (!element || window.pyunhangaPharmacyCart?.isolated) return;
  const config = JSON.parse(element.textContent), root = config.root, text = config.text;
  const storageKey = 'pyunhanga:carts:v2:' + config.customer;
  const validPage = value => typeof value === 'string' && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) && value.length <= 150 ? value : '';
  const object = entries => Object.fromEntries((entries || []).map(({key, value}) => [key, value]));
  const attributes = value => Object.entries(value).map(([key, value]) => ({key, value: String(value ?? '')}));
  const token = id => id.replace(/^gid:\/\/shopify\/Cart\//, '').split('?')[0];
  const lineFields = 'id quantity attributes{key value} cost{totalAmount{amount currencyCode}} merchandise{...on ProductVariant{id title image{url altText} product{id title handle}}} sellingPlanAllocation{sellingPlan{id name}}';
  const cartFields = 'id totalQuantity attributes{key value} cost{subtotalAmount{amount currencyCode} totalAmount{amount currencyCode}}';
  const queries = {
    read: `query PharmacyCart($id:ID!,$after:String){cart(id:$id){${cartFields} lines(first:50,after:$after){nodes{${lineFields}} pageInfo{hasNextPage endCursor}}}}`,
    create: 'mutation PharmacyCreate($input:CartInput){cartCreate(input:$input){cart{id} userErrors{message} warnings{message}}}',
    add: 'mutation PharmacyAdd($cartId:ID!,$lines:[CartLineInput!]!){cartLinesAdd(cartId:$cartId,lines:$lines){cart{id} userErrors{message} warnings{message}}}',
    update: 'mutation PharmacyUpdate($cartId:ID!,$lines:[CartLineUpdateInput!]!){cartLinesUpdate(cartId:$cartId,lines:$lines){cart{id} userErrors{message} warnings{message}}}',
    remove: 'mutation PharmacyRemove($cartId:ID!,$lineIds:[ID!]!){cartLinesRemove(cartId:$cartId,lineIds:$lineIds){cart{id} userErrors{message} warnings{message}}}',
    attributes: 'mutation PharmacyAttributes($cartId:ID!,$attributes:[AttributeInput!]!){cartAttributesUpdate(cartId:$cartId,attributes:$attributes){cart{id} userErrors{message}}}',
    buyer: 'mutation PharmacyBuyer($cartId:ID!,$buyerIdentity:CartBuyerIdentityInput!){cartBuyerIdentityUpdate(cartId:$cartId,buyerIdentity:$buyerIdentity){cart{id} userErrors{message}}}',
    checkout: 'query PharmacyCheckout($id:ID!){cart(id:$id){id checkoutUrl attributes{key value} totalQuantity}}',
    page: 'query PharmacyName($handle:String!){page(handle:$handle){title}}'
  };
  let page = validPage(config.page) || validPage(new URL(location.href).searchParams.get('pharmacy'));
  if (!page) { try { page = validPage(sessionStorage.getItem('pharmacy_page')); } catch {} }
  let state = null, busy = false, startPromise, checkoutPromise;
  const cartURL = (address = false) => root + 'cart?' + new URLSearchParams({pharmacy: page, ...(address ? {pharmacy_checkout:'address'} : {})});
  const storeURL = selected => root + 'pages/' + encodeURIComponent(selected || page);
  function index() {
    try { const value = JSON.parse(localStorage.getItem(storageKey) || '{"carts":{}}'); if (!value.carts || typeof value.carts !== 'object') throw Error(); return value; }
    catch { throw Error(text.storage); }
  }
  function persist(value) {
    try { localStorage.setItem(storageKey, JSON.stringify(value)); }
    catch { throw Error(text.storage); }
  }
  async function locked(task) {
    if (!navigator.locks?.request) throw Error(text.browser);
    return navigator.locks.request('pyunhanga:carts:v2', task);
  }
  async function request(url, options = {}) {
    const response = await fetch(url, {...options, signal: AbortSignal.timeout(20000), headers: {'Content-Type':'application/json', Accept:'application/json', ...(options.headers || {})}});
    const result = await response.json();
    if (!response.ok) throw Error(result.error || result.description || text.failed);
    return result;
  }
  async function graphql(operation, variables) {
    const result = await request('/api/2026-10/graphql.json', {method:'POST', body:JSON.stringify({query:queries[operation], variables})});
    if (result.errors?.length) throw Error(text.failed);
    if (!result.data) throw Error(text.failed);
    return result.data;
  }
  async function mutate(operation, variables) {
    const result = Object.values(await graphql(operation, variables))[0];
    if (result?.userErrors?.length) throw Error(result.userErrors.map(error => error.message).join(' '));
    if (!result?.cart?.id) throw Error(text.failed);
    if (result.warnings?.length) message(result.warnings.map(warning => warning.message).join(' '));
    return result.cart;
  }
  function assertCart(cart, selected) {
    if (object(cart.attributes)._pharmacy_page !== selected) throw Error(text.mismatch);
    if (cart.lines.nodes.some(line => object(line.attributes)._pharmacy_page !== selected)) throw Error(text.mismatch);
  }
  async function readCart(id, selected) {
    let cart, after = null, lines = [];
    do {
      const result = (await graphql('read', {id, after})).cart;
      if (!result) return null;
      cart = result; lines.push(...result.lines.nodes);
      after = result.lines.pageInfo.hasNextPage ? result.lines.pageInfo.endCursor : null;
      if (result.lines.pageInfo.hasNextPage && !after) throw Error(text.failed);
    } while (after);
    cart.lines.nodes = lines;
    assertCart(cart, selected);
    return cart;
  }
  async function getCart(selected, create = false) {
    const saved = index();
    let entry = saved.carts[selected], cart = entry?.id ? await readCart(entry.id, selected) : null;
    if (!cart && create) {
      const created = await mutate('create', {input:{attributes:attributes({_pharmacy_page:selected}), buyerIdentity:{countryCode:'KR'}}});
      entry = {...entry, id:created.id}; saved.carts[selected] = entry; persist(saved);
      cart = await readCart(created.id, selected);
    }
    return cart;
  }
  async function authorize(cart, selected, variantId) {
    assertCart(cart, selected);
    const ctx = await request(config.endpoint.replace(/\/$/, '') + '/attribution', {method:'POST', body:JSON.stringify({page:selected, cartToken:token(cart.id), previousToken:object(cart.attributes)._pharmacy_context, productIds:[...new Set(cart.lines.nodes.map(line => line.merchandise.product.id))], variantIds:variantId ? [variantId] : []})});
    if (ctx.page !== selected || !ctx.pharmacy || !ctx.token) throw Error(text.mismatch);
    return {_pharmacy_page:ctx.page, _pharmacy_handle:ctx.pharmacy, _pharmacy_context:ctx.token};
  }
  async function saveAttributes(cart, extra) {
    await mutate('attributes', {cartId:cart.id, attributes:attributes({...object(cart.attributes), ...extra})});
    cart.attributes = attributes({...object(cart.attributes), ...extra});
  }
  async function migrateGuest() {
    if (config.customer === 'guest') return;
    const guestKey = 'pyunhanga:carts:v2:guest';
    let guest;
    try {guest = JSON.parse(localStorage.getItem(guestKey) || '{"carts":{}}');}
    catch {throw Error(text.storage);}
    for (const [selected, entry] of Object.entries(guest.carts || {})) {
      if (!validPage(selected) || !entry.id) continue;
      const saved = index(), current = saved.carts[selected];
      if (!current?.id || current.id === entry.id) saved.carts[selected] = entry;
      else {
        const from = await readCart(entry.id, selected), into = await readCart(current.id, selected);
        if (!into) saved.carts[selected] = entry;
        else if (from?.totalQuantity) {
          const present = new Set(into.lines.nodes.map(line => object(line.attributes)._pharmacy_import_line));
          const lines = [];
          for (const line of from.lines.nodes) {
            const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(line.id));
            const key = Array.from(new Uint8Array(digest), value => value.toString(16).padStart(2,'0')).join('');
            if (present.has(key)) continue;
            const props = {...object(line.attributes), _pharmacy_import_line:key};
            delete props._pharmacy_context;
            const input = {merchandiseId:line.merchandise.id, quantity:line.quantity, attributes:attributes(props)};
            if (line.sellingPlanAllocation) input.sellingPlanId = line.sellingPlanAllocation.sellingPlan.id;
            lines.push(input);
          }
          for (let offset=0; offset<lines.length; offset+=100) await mutate('add',{cartId:into.id,lines:lines.slice(offset,offset+100)});
        }
      }
      if (guest.legacyChecked) saved.legacyChecked = true;
      if (!saved.last) saved.last = guest.last;
      persist(saved);
      delete guest.carts[selected];
      localStorage.setItem(guestKey, JSON.stringify(guest));
    }
  }
  async function migrateLegacy() {
    const saved = index();
    if (saved.legacyChecked || localStorage.getItem('pyunhanga:native-cart-migrated:v2')) {
      saved.legacyChecked = true; persist(saved); return;
    }
    const legacy = await request(root + 'cart.js', {cache:'no-store'});
    const selected = validPage(legacy.attributes?._pharmacy_page);
    if (legacy.item_count && selected && !saved.carts[selected]) {
      if (legacy.items.some(item => item.properties?._pharmacy_page !== selected)) throw Error(text.mismatch);
      const created = await mutate('create', {input:{attributes:attributes({_pharmacy_page:selected}), buyerIdentity:{countryCode:'KR'}}});
      saved.carts[selected] = {id:created.id, legacyPending:true}; persist(saved);
    }
    const pending = saved.carts[selected];
    if (legacy.item_count && selected && pending?.legacyPending) {
      let cart = await readCart(pending.id, selected);
      if (!cart) throw Error(text.failed);
      const existing = new Set(cart.lines.nodes.map(line => object(line.attributes)._pharmacy_legacy_line));
      const pendingLines = [];
      for (const item of legacy.items) {
        if (existing.has(item.key)) continue;
        const props = {...item.properties, _pharmacy_page:selected, _pharmacy_legacy_line:item.key};
        delete props._pharmacy_context;
        const input = {merchandiseId:'gid://shopify/ProductVariant/' + item.variant_id, quantity:item.quantity, attributes:attributes(props)};
        if (item.selling_plan_allocation?.selling_plan?.id) input.sellingPlanId = 'gid://shopify/SellingPlan/' + item.selling_plan_allocation.selling_plan.id;
        pendingLines.push(input);
      }
      for (let offset = 0; offset < pendingLines.length; offset += 100) await mutate('add', {cartId:cart.id, lines:pendingLines.slice(offset, offset + 100)});
      delete pending.legacyPending;
    }
    saved.legacyChecked = true;
    localStorage.setItem('pyunhanga:native-cart-migrated:v2', '1');
    if (!page) page = selected || validPage(saved.last);
    persist(saved);
  }
  async function nameFor(selected) {
    const saved = index(), entry = saved.carts[selected] || {};
    if (entry.name) return entry.name;
    const name = selected === config.page && config.name ? config.name : (await graphql('page', {handle:selected})).page?.title;
    if (!name) throw Error(text.missing);
    const latest = index(); latest.carts[selected] = {...latest.carts[selected], name}; persist(latest);
    return name;
  }
  async function refresh() {
    if (!page) return render(null);
    const cart = await getCart(page);
    const name = await nameFor(page);
    state = {cart, name}; render(state); return cart;
  }
  async function initialize() {
    if (startPromise) return startPromise;
    startPromise = locked(async () => {
      persist(index());
      await migrateGuest();
      await migrateLegacy();
      if (!page) page = validPage(index().last);
      if (page) {
        sessionStorage.setItem('pharmacy_page', page);
        const saved = index(); saved.last = page; persist(saved);
      }
      syncLinks();
      return refresh();
    }).catch(error => {startPromise = null; throw error;});
    return startPromise;
  }
  async function add(data) {
    await initialize();
    const selected = page, variant = String(data.get('id') || ''), quantity = Number(data.get('quantity') || 1);
    if (!selected) throw Error(text.choose);
    if (!/^\d+$/.test(variant) || !Number.isSafeInteger(quantity) || quantity < 1) throw Error(text.quantity);
    const props = {};
    for (const [key, value] of data) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && !match[1].startsWith('_pharmacy_')) {
        if (typeof value !== 'string') throw Error(text.unsupported);
        props[match[1]] = value;
      }
    }
    return locked(async () => {
      const cart = await getCart(selected, true), merchandiseId = 'gid://shopify/ProductVariant/' + variant;
      const ctx = await authorize(cart, selected, merchandiseId);
      const line = {merchandiseId, quantity, attributes:attributes({...props, ...ctx})};
      if (data.get('selling_plan')) line.sellingPlanId = 'gid://shopify/SellingPlan/' + String(data.get('selling_plan'));
      await mutate('add', {cartId:cart.id, lines:[line]});
      await saveAttributes(cart, ctx);
      await refresh();
      return ctx;
    });
  }
  async function change(lineId, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0) throw Error(text.quantity);
    return locked(async () => {
      const cart = await getCart(page);
      if (!cart?.lines.nodes.some(line => line.id === lineId)) throw Error(text.changed);
      if (quantity) {
        const ctx = await authorize(cart, page);
        const line = cart.lines.nodes.find(item => item.id === lineId);
        await mutate('update', {cartId:cart.id, lines:[{id:lineId, quantity, attributes:attributes({...object(line.attributes), ...ctx})}]});
      } else await mutate('remove', {cartId:cart.id, lineIds:[lineId]});
      return refresh();
    });
  }
  async function prepareCheckout(details, destination, selected, expectedId) {
    return locked(async () => {
      const cart = await getCart(selected);
      if (!cart?.totalQuantity || cart.id !== expectedId) throw Error(text.changed);
      const ctx = await authorize(cart, selected);
      for (let offset = 0; offset < cart.lines.nodes.length; offset += 100) {
        await mutate('update', {cartId:cart.id, lines:cart.lines.nodes.slice(offset, offset + 100).map(line => ({id:line.id, quantity:line.quantity, attributes:attributes({...object(line.attributes), ...ctx})}))});
      }
      const attempt = crypto.randomUUID();
      await saveAttributes(cart, {...ctx, ...details, __ph_membership_attempt__:attempt, __ph_membership_receipt__:''});
      await mutate('buyer', {cartId:cart.id, buyerIdentity:{countryCode:'KR', email:details['주문자 이메일']}});
      await window.pyunhangaMembership?.prepareCheckout(attempt, {
        read:async () => ({token:token(cart.id)}),
        update:extra => saveAttributes(cart, extra)
      });
      const verified = await readCart(cart.id, selected);
      if (!verified?.totalQuantity || object(verified.attributes)._pharmacy_context !== ctx._pharmacy_context || verified.lines.nodes.some(line => object(line.attributes)._pharmacy_context !== ctx._pharmacy_context || object(line.attributes)._pharmacy_handle !== ctx._pharmacy_handle)) throw Error(text.mismatch);
      const checkoutCart = (await graphql('checkout', {id:cart.id})).cart;
      if (!checkoutCart?.totalQuantity || object(checkoutCart.attributes)._pharmacy_page !== selected) throw Error(text.mismatch);
      const url = new URL(checkoutCart.checkoutUrl);
      if (url.protocol !== 'https:' || ![location.hostname, config.shopDomain].includes(url.hostname)) throw Error(text.failed);
      for (const [key, value] of new URL(destination, location.origin).searchParams) if (key.startsWith('checkout[')) url.searchParams.set(key, value);
      return url.href;
    });
  }
  async function checkout() {
    if (checkoutPromise) return checkoutPromise;
    checkoutPromise = (async () => {
      await initialize();
      const selected = page, cart = await locked(() => getCart(selected));
      if (!cart?.totalQuantity) throw Error(text.empty);
      const collector = document.querySelector('pharmacy-checkout-address');
      if (!collector) { location.assign(cartURL(true)); return; }
      let timer;
      try { await Promise.race([customElements.whenDefined('pharmacy-checkout-address'), new Promise((_, reject) => {timer = setTimeout(() => reject(Error(text.failed)), 15000);})]); }
      finally {clearTimeout(timer);}
      const destination = await collector.request(root, {save:(details, url) => prepareCheckout(details, url, selected, cart.id)});
      if (destination) location.assign(destination);
    })().finally(() => {checkoutPromise = null;});
    return checkoutPromise;
  }
  function message(value) {
    let node = document.getElementById('pharmacy-context-status');
    if (!node) {node = document.createElement('p'); node.id = 'pharmacy-context-status'; node.setAttribute('role','alert'); document.body.append(node);}
    node.textContent = value;
  }
  function syncLinks() {
    if (!page) return;
    for (const anchor of document.querySelectorAll('a[href]')) {
      const url = new URL(anchor.href, location.href);
      if (url.origin !== location.origin) continue;
      if (url.pathname === root + 'cart' || url.pathname === root + 'cart/') {
        url.searchParams.set('pharmacy', page);
      } else if (url.pathname.startsWith(root + 'products/')) {
        url.searchParams.set('pharmacy', page);
      } else if (url.pathname === root + 'collections/all' && anchor.closest('main')) {
        url.pathname = storeURL();
      } else continue;
      const target = url.pathname + url.search + url.hash;
      if (anchor.getAttribute('href') !== target) anchor.setAttribute('href', target);
    }
  }
  function node(tag, label, className) {
    const result = document.createElement(tag);
    if (label != null) result.textContent = label;
    if (className) result.className = className;
    return result;
  }
  function money(value) {return new Intl.NumberFormat(config.locale || 'ko-KR', {style:'currency', currency:value.currencyCode}).format(Number(value.amount));}
  function syncCounts(value = state) {
    const cart = value?.cart, count = cart?.totalQuantity || 0;
    for (const bubble of document.querySelectorAll('[ref="cartBubble"]')) {
      bubble.classList.toggle('visually-hidden', !count);
      const label = bubble.querySelector('[ref="cartBubbleCount"]');
      if (label) {if (label.textContent !== String(count)) label.textContent = String(count); label.classList.toggle('hidden', !count);}
    }
    for (const header of document.querySelectorAll('ph-store-header')) {
      const label = header.querySelector('[data-header-cart-count]'), link = header.querySelector('[data-header-cart]');
      const displayCount = count > 99 ? '99+' : String(count);
      if (label && label.textContent !== displayCount) label.textContent = displayCount;
      if (link && header.dataset.cartLabel) {
        const description = header.dataset.cartLabel.replace('__COUNT__', String(count));
        if (link.getAttribute('aria-label') !== description) link.setAttribute('aria-label', description);
      }
    }
  }
  function render(value) {
    const cart = value?.cart;
    syncCounts(value);
    const panel = document.querySelector('[data-pharmacy-cart-panel]');
    if (!panel) return;
    panel.removeAttribute('aria-busy'); panel.replaceChildren();
    panel.append(node('h1', value ? value.name + ' ' + text.title : text.title));
    const back = node('a', text.continue, 'pcart-back'); back.href = page ? storeURL() : root; panel.append(back);
    if (!cart?.totalQuantity) {panel.append(node('p', page ? text.empty : text.choose)); syncLinks(); return;}
    const list = node('ul', null, 'pcart-lines');
    for (const line of cart.lines.nodes) {
      const product = line.merchandise, li = node('li', null, 'pcart-line');
      if (product.image?.url) {const image = node('img'); image.src = product.image.url; image.alt = product.image.altText || product.product.title; image.width = 88; image.height = 88; image.loading = 'lazy'; li.append(image);}
      const info = node('div', null, 'pcart-info'), link = node('a', product.product.title);
      link.href = root + 'products/' + encodeURIComponent(product.product.handle) + '?' + new URLSearchParams({variant:product.id.split('/').pop(), pharmacy:page}); info.append(link);
      if (product.title !== 'Default Title') info.append(node('p', product.title));
      if (line.sellingPlanAllocation) info.append(node('p', line.sellingPlanAllocation.sellingPlan.name));
      for (const [key, value] of Object.entries(object(line.attributes))) if (!key.startsWith('_')) info.append(node('p', key + ': ' + value));
      li.append(info);
      const controls = node('div', null, 'pcart-controls'), label = node('label', text.quantity_label), input = node('input');
      input.type = 'number'; input.min = '1'; input.step = '1'; input.value = String(line.quantity); input.setAttribute('aria-label', product.product.title + ' ' + text.quantity_label); label.append(input); controls.append(label);
      input.addEventListener('change', () => runChange(input, line.id, Number(input.value)));
      const remove = node('button', text.remove); remove.type = 'button'; remove.addEventListener('click', () => runChange(remove, line.id, 0)); controls.append(remove); li.append(controls, node('strong', money(line.cost.totalAmount))); list.append(li);
    }
    panel.append(list);
    const summary = node('div', null, 'pcart-summary'); summary.append(node('p', text.total + ' ' + money(cart.cost.subtotalAmount)), node('small', text.shipping));
    const button = node('button', text.checkout, 'pcart-checkout'); button.type = 'button'; button.addEventListener('click', async () => {button.disabled = true; try {await checkout();} catch(error) {message(error.message);} finally {button.disabled = false;}}); summary.append(button); panel.append(summary); syncLinks();
  }
  async function runChange(control, lineId, quantity) {
    const panel = document.querySelector('[data-pharmacy-cart-panel]');
    if (panel?.getAttribute('aria-busy') === 'true') return;
    panel?.setAttribute('aria-busy','true'); control.disabled = true;
    try {await change(lineId, quantity);} catch(error) {message(error.message); try {await locked(refresh);} catch {} }
    finally {panel?.removeAttribute('aria-busy'); control.disabled = false;}
  }
  let buyNavigation = null;
  function markBuy(host, stage, notice) {
    if (!host) return;
    if (stage) host.setAttribute('data-pcart-buy-stage', stage);
    else host.removeAttribute('data-pcart-buy-stage');
    host.sync?.();
    host.showStatus?.(notice);
  }
  window.addEventListener('pageshow', event => {
    if (!event.persisted || !buyNavigation) return;
    const {host, button} = buyNavigation;
    buyNavigation = null; busy = false;
    if (!host?.sync && button) button.disabled = false;
    markBuy(host, '', '');
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const url = new URL(form.action, location.href);
    if (url.origin !== location.origin || !/\/cart\/add(?:\.js)?$/.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy || !form.reportValidity()) return;
    const data = new FormData(form), button = event.submitter, buyNow = button?.hasAttribute('data-buy-now');
    const host = buyNow ? form.closest('pharmacy-purchase-controls') : null;
    busy = true;
    if (buyNow) markBuy(host, 'preparing', '배송지 입력 화면을 준비하고 있습니다.');
    if (button) button.disabled = true;
    let leaving = false;
    try {
      await add(data);
      if (buyNow) {
        markBuy(host, 'opening', '배송지 입력 화면으로 이동합니다.');
        location.assign(cartURL(true));
        leaving = true; buyNavigation = {host, button};
        return;
      }
      const feedback = document.querySelector('pharmacy-cart-feedback');
      if (feedback?.show) feedback.show(text.added); else message(text.added);
      const link = form.closest('pharmacy-purchase-controls')?.querySelector('[data-purchase-cart-link]');
      if (link) {link.hidden = false; link.href = cartURL();}
    } catch(error) {
      const notice = error?.message || text.failed;
      if (buyNow) markBuy(host, '', notice);
      if (!host?.showStatus) message(notice);
    } finally {
      if (!leaving) {
        busy = false;
        if (button && !host?.sync) button.disabled = false;
        else host?.sync?.();
      }
    }
  }, true);
  document.addEventListener('click', event => {
    const target = event.target.closest('a[href], [data-header-cart], [data-cart-feedback-open], [on\\:click*="cart-drawer"]');
    if (!target) return;
    const url = target.matches('a[href]') ? new URL(target.href, location.href) : null;
    const isCart = target.matches('[data-header-cart], [data-cart-feedback-open], [on\\:click*="cart-drawer"]') || url?.origin === location.origin && /\/cart\/?$/.test(url.pathname);
    const isCheckout = url?.origin === location.origin && /\/checkout\/?$/.test(url.pathname);
    if (!isCart && !isCheckout) return;
    if (isCart && event.button !== 0) return;
    if (isCart && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) && target.matches('a[href]')) {target.href = cartURL(); event.stopImmediatePropagation(); return;}
    event.preventDefault(); event.stopImmediatePropagation();
    if (isCheckout) checkout().catch(error => message(error.message));
    else initialize().then(() => location.assign(cartURL())).catch(error => message(error.message));
  }, true);
  window.pyunhangaPharmacyCart = Object.freeze({isolated:true, add, checkout, ensure:async () => {await initialize(); return page ? {page} : null;}, relevant:() => Boolean(page), cartURL});
  function start() {
    new MutationObserver(() => {syncLinks(); syncCounts();}).observe(document.body, {subtree:true, childList:true}); syncLinks();
    initialize().catch(error => {message(error.message); const panel = document.querySelector('[data-pharmacy-cart-panel]'); if (panel) {panel.removeAttribute('aria-busy'); panel.replaceChildren(node('p',text.failed)); const retry = node('button',text.retry); retry.type='button'; retry.onclick=()=>initialize().catch(error=>message(error.message)); panel.append(retry);}});
  }
  window.addEventListener('storage', event => {if (event.key === storageKey && !busy) locked(refresh).catch(error => message(error.message));});
  window.addEventListener('pageshow', event => {if (event.persisted) locked(refresh).catch(error => message(error.message));});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
